import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PremiumContext = createContext();

const DAILY_FREE_LIMIT = 10;
const PREMIUM_PRICE = "$1.99";
const PREMIUM_PERIOD = "month";

export const PremiumProvider = ({ children }) => {
  const [isPremium, setIsPremium] = useState(false);
  const [questionsToday, setQuestionsToday] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lastResetDate, setLastResetDate] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);

  // Callback for when iOS StoreKit restore succeeds
  const handleRestoreSuccess = useCallback((expirationDate) => {
    console.log("Restore successful, expiration:", expirationDate);
    setIsPremium(true);
    setShowUpgradeModal(false);
    setSubscriptionEndDate(expirationDate || null);
    // Also store locally as backup
    localStorage.setItem('nexi_premium_purchased', 'true');
  }, []);

  // Callback for when iOS StoreKit restore fails
  const handleRestoreFailed = useCallback((message) => {
    console.log("Restore failed:", message);
    alert(message || 'No previous subscription found.');
  }, []);

  // Callback for when iOS StoreKit purchase succeeds
  const handlePurchaseSuccess = useCallback((expirationDate) => {
    console.log("Purchase successful, expiration:", expirationDate);
    setIsPremium(true);
    setShowUpgradeModal(false);
    if (expirationDate) {
      setSubscriptionEndDate(expirationDate);
    } else {
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);
      setSubscriptionEndDate(endDate.toISOString());
    }
    localStorage.setItem('nexi_premium_purchased', 'true');
  }, []);

  // Register global handlers for iOS StoreKit callbacks
  useEffect(() => {
    // These functions will be called by the native iOS app via JavaScript injection
    window.nexiRestoreSuccess = (expirationDate) => handleRestoreSuccess(expirationDate);
    window.nexiRestoreFailed = (message) => handleRestoreFailed(message);
    window.nexiPurchaseSuccess = (expirationDate) => handlePurchaseSuccess(expirationDate);
    window.nexiPurchaseFailed = (message) => alert(message || 'Purchase failed. Please try again.');

    return () => {
      delete window.nexiRestoreSuccess;
      delete window.nexiRestoreFailed;
      delete window.nexiPurchaseSuccess;
      delete window.nexiPurchaseFailed;
    };
  }, [handleRestoreSuccess, handleRestoreFailed, handlePurchaseSuccess]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('nexi_premium_status');
    if (stored) {
      const data = JSON.parse(stored);
      
      // Check if subscription is still active
      if (data.subscriptionEndDate) {
        const endDate = new Date(data.subscriptionEndDate);
        if (endDate > new Date()) {
          setIsPremium(true);
          setSubscriptionEndDate(data.subscriptionEndDate);
        } else {
          setIsPremium(false);
          setSubscriptionEndDate(null);
        }
      } else {
        setIsPremium(data.isPremium || false);
      }
      
      // Check if it's a new day - reset counter
      const today = new Date().toDateString();
      if (data.lastResetDate !== today) {
        setQuestionsToday(0);
        setLastResetDate(today);
      } else {
        setQuestionsToday(data.questionsToday || 0);
        setLastResetDate(data.lastResetDate);
      }
    } else {
      setLastResetDate(new Date().toDateString());
    }
    
    // Also check the simple premium purchased flag (for restore after reinstall)
    const wasPurchased = localStorage.getItem('nexi_premium_purchased');
    if (wasPurchased === 'true' && !isPremium) {
      // User previously purchased but local status was lost
      // The iOS app should verify with Apple on launch
      console.log("Previous purchase detected, waiting for iOS verification...");
    }
  }, []);

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem('nexi_premium_status', JSON.stringify({
      isPremium,
      questionsToday,
      lastResetDate: lastResetDate || new Date().toDateString(),
      subscriptionEndDate
    }));
  }, [isPremium, questionsToday, lastResetDate, subscriptionEndDate]);

  // Check if user can ask a question
  const canAskQuestion = () => {
    if (isPremium) return true;
    return questionsToday < DAILY_FREE_LIMIT;
  };

  // Get remaining questions
  const getRemainingQuestions = () => {
    if (isPremium) return Infinity;
    return Math.max(0, DAILY_FREE_LIMIT - questionsToday);
  };

  // Use a question (increment counter)
  const useQuestion = () => {
    if (!isPremium) {
      setQuestionsToday(prev => prev + 1);
    }
  };

  // Upgrade to premium (called after successful Apple IAP)
  const upgradeToPremium = () => {
    setIsPremium(true);
    setShowUpgradeModal(false);
    // Set subscription end date to 1 month from now (for display purposes)
    // In production, this comes from Apple's StoreKit transaction
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 1);
    setSubscriptionEndDate(endDate.toISOString());
  };

  // Restore purchase
  const restorePurchase = () => {
    // In real app, this would check with Apple for previous purchases
    const stored = localStorage.getItem('nexi_premium_purchased');
    if (stored === 'true') {
      setIsPremium(true);
      return true;
    }
    return false;
  };

  // Prompt upgrade
  const promptUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const value = {
    isPremium,
    questionsToday,
    canAskQuestion,
    getRemainingQuestions,
    useQuestion,
    upgradeToPremium,
    restorePurchase,
    promptUpgrade,
    showUpgradeModal,
    setShowUpgradeModal,
    DAILY_FREE_LIMIT,
    PREMIUM_PRICE,
    PREMIUM_PERIOD,
    subscriptionEndDate
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremium = () => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
};

export default PremiumContext;
